import { useStaffText } from '../ui/StaffText'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { gridServices, hiddenOn } from '../lib/kioskPages'
import { languageButtons, serviceName, type Language } from '../lib/kioskText'
import type { Branch, Service } from '../lib/types'
import { LayoutSurface } from './render'
import { sampleBindings } from './sample'
import { usePlayedDocument } from './previewPlaylists'
import { pageIds, textIn, type ButtonAction, type LayoutDocument } from './types'
import { Button, ToggleButton } from '@heroui/react'
import { StudioSelect } from './heroParts'
import { startsAtWelcome } from '../lib/kioskStart'
import { branchOptionOn, useBranchSettings } from '../lib/branchSettings'
import { useSession } from '../ui/Session'
import { bookingHidden } from './bookingButton'

type Step =
  | { at: 'attract' }
  | { at: 'choosing'; page: string; trail: string[] }
  | { at: 'issued'; service: string }

/**
 * The design as a kiosk at one branch: tap through the welcome, the pages, back,
 * and a service, with the buttons that branch would hide hidden. What it misses
 * or hides is said in the editor's issue list, not here.
 *
 * It draws with the kiosk's own renderer and asks lib/kioskPages the same
 * questions the kiosk asks, so what is seen here is what that branch's lobby will
 * show. Nothing is issued: choosing a service shows the ticket screen with a
 * sample number.
 */
export function KioskPreview({
  document: doc,
  branches,
  branchId,
  offered,
  language: initialLanguage,
  languages: offeredLanguages,
  assetURL,
  onBranch,
}: {
  document: LayoutDocument
  branches: Branch[]
  branchId: string
  /** The branch's active services; null while they load. */
  offered: Service[] | null
  language: string
  /** The design's languages, in tab order: the switch offers them. */
  languages: readonly string[]
  assetURL: (id: string) => string
  onBranch: (id: string) => void
}) {
  const { say } = useStaffText()
  const booking = branchOptionOn(useBranchSettings(branchId), useSession()?.identity, 'onlineBooking')
  const playing = usePlayedDocument(doc)
  const played = playing
  const menu = pageIds(doc)[0]!
  // The first step: the welcome, or the menu in the first language when the design skips it.
  const start: Step = startsAtWelcome(doc) ? { at: 'attract' } : { at: 'choosing', page: menu, trail: [] }
  const [step, setStep] = useState<Step>(start)
  const [lang, setLang] = useState(initialLanguage)
  const restart = () => {
    setStep(start)
    setLang(offeredLanguages[0] ?? initialLanguage)
  }
  // Drawn at the panel's real pixels, scrolled when bigger than the stage.
  const [actual, setActual] = useState(false)
  const branch = branches.find((b) => b.id === branchId)

  const page = step.at === 'choosing' ? step.page : menu
  const hidden = useMemo(
    () => new Set<string>([...(offered ? hiddenOn(doc, offered) : []), ...bookingHidden(played, booking)]),
    [doc, offered, played, booking],
  )
  const name = (s: Service) => serviceName(s.name, lang as Language, s.code)

  const press = (action: ButtonAction) => {
    if (action.kind === 'service') {
      const s = offered?.find((o) => o.id === action.serviceId)
      setStep({ at: 'issued', service: s ? name(s) : '' })
    } else if (action.kind === 'page' && step.at === 'choosing') {
      setStep({ at: 'choosing', page: action.pageId, trail: [...step.trail, step.page] })
    } else if (action.kind === 'back' && step.at === 'choosing') {
      const trail = step.trail
      if (trail.length === 0) restart()
      else setStep({ at: 'choosing', page: trail[trail.length - 1]!, trail: trail.slice(0, -1) })
    }
  }

  const sample = sampleBindings(branch?.name ?? '', lang)
  const screen = step.at === 'attract' ? 'attract' : step.at === 'choosing' ? 'choosing' : 'issued'

  return (
    <div className="studio-preview">
      <div className="studio-preview__bar">
        <StudioSelect
          className="studio-preview__branch"
          aria-label={say('designs.page.branch')}
          value={branchId}
          onChange={(value) => {
            restart()
            onBranch(value)
          }}
          options={branches.map((b) => ({ value: b.id, label: b.name }))}
        />
        <Button variant="secondary" size="sm" onPress={restart}>
          {say('designs.ed.startAgain')}
        </Button>
        <ToggleButton size="sm" isSelected={actual} onChange={setActual}>
          {say('designs.zoom.actual')}
        </ToggleButton>
      </div>
      <div className="studio-preview__screen" data-actual={actual || undefined} onClick={step.at === 'attract' ? () => setStep({ at: 'choosing', page: menu, trail: [] }) : undefined}>
        <LayoutSurface
          document={played}
          screen={screen}
          scale={actual ? 1 : undefined}
          bindings={{
            ...sample,
            language: lang,
            assetURL,
            playing: true,
            page,
            hidden,
            services: (offered ?? []).map((s) => ({ id: s.id, prefix: '', name: name(s) })),
            servicesFor: (grid) =>
              gridServices(grid, offered ?? [], page === menu).map((s) => ({ id: s.id, prefix: '', name: name(s) })),
            onPickService: (id) => press({ kind: 'service', serviceId: id }),
            serviceLabel: (id) => {
              const s = offered?.find((o) => o.id === id)
              return s ? name(s) : ''
            },
            pageLabel: (id) => textIn(doc.pages?.find((p) => p.id === id)?.name, lang),
            onButton: press,
            languages: languageButtons(offeredLanguages),
            onPickLanguage: (code) => {
              setLang(code)
              if (step.at === 'attract') setStep({ at: 'choosing', page: menu, trail: [] })
            },
            ticketService: step.at === 'issued' ? step.service : sample.ticketService,
          }}
        />
      </div>
    </div>
  )
}

/** A branch's active services, for the preview and the issue list; null until known. */
export function useBranchOffer(branchId: string | undefined): Service[] | null {
  const [got, setGot] = useState<{ id: string; services: Service[] } | null>(null)
  useEffect(() => {
    if (!branchId) return
    let live = true
    api
      .services(branchId)
      .then((r) => live && setGot({ id: branchId, services: r.services.filter((s) => s.active) }))
      // Unknown, not empty: an empty offer would call every button hidden.
      .catch(() => live && setGot(null))
    return () => {
      live = false
    }
  }, [branchId])
  return got && got.id === branchId ? got.services : null
}
